/**
 * Tour Management — custom hook containing all state, data loading, and CRUD handlers.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

import type { KstDataGridPager, KstDataGridRows } from '@/components/KstDataGrid';

// ── External Declarations ────────────────────────────────────────────

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import moment from 'moment';
import { hasValue, hasStringValue, getOfficerLabel } from '@/utils/legacy-utils';
import { Tour, Task, TourStop, TourExemption, TourStopTask, GuardTourStop, UpdateOfficerComments, DataObjectLocationFields } from '@/models/legacy/index';
import { WeekDays, SelectableTimeZones, SelectableEnabledTourStopDaysTypes } from '@/constants/codes';
import { OfficerCommentsRequired } from '@/models/tour';
import { DATETIME_FORMAT_DISPLAY_LOCAL_MOMENT } from '@/constants/formats';
import { GOOGLE_MAPS_BROWSER_API_KEY } from '@/constants/google-maps';
const google = (window as any).google;

import { getOfficerPluralLabel, getCurrentCompanyId } from '@/utils/auth';
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const deepCopyProperties = _wg('deepCopyProperties');
const runPromiseFactoriesSequentially = _wg('runPromiseFactoriesSequentially');
import { validateAddressWithSuggestions } from '@/utils/custom-location';
import { getMapStyleForCurrentTheme } from '@/services/map-service';
const fillInAddressFromSuggestions = _wg('fillInAddressFromSuggestions');
const addressValidationErrorMsg = "Error validating location. Please re-enter the location address.";
const getGoogleMapsPolygonBounds = _wg('getGoogleMapsPolygonBounds');

// ── Types ────────────────────────────────────────────────────────────

export interface DeleteConfirmState {
  open: boolean;
  title: string;
  message: string;
  items: any[];
  deleteType: string;
}

export interface CopyTourModalState {
  open: boolean;
  withStops: boolean;
}

// ── Sub-tab URL mapping ──────────────────────────────────────────────

const subTabConfig: Record<string, { activeTab: string; page: string }> = {
  'tours':      { activeTab: 'showToursTab',            page: 'showManagement' },
  'stops':      { activeTab: 'showTourStopsTab',        page: 'showTourStopList' },
  'tasks':      { activeTab: 'showTourStopTasksTab',    page: 'showTaskList' },
  'exceptions': { activeTab: 'showExemptionsTab',       page: 'showExemptionList' },
  'logs':       { activeTab: 'showLogsTab',             page: 'showAllTourLogs' },
  'history':    { activeTab: 'showGuardTourHistoryTab',  page: 'showGuardTourHistory' },
  'reminders':  { activeTab: 'showRemindersTab',         page: 'showReminders' },
};

const activeTabToSlug: Record<string, string> = {
  'showToursTab':            'tours',
  'showTourStopsTab':        'stops',
  'showTourStopTasksTab':    'tasks',
  'showExemptionsTab':       'exceptions',
  'showLogsTab':             'logs',
  'showGuardTourHistoryTab': 'history',
  'showRemindersTab':        'reminders',
};

// ── Hook ─────────────────────────────────────────────────────────────

export function useTourManagement(initialSubTab?: string) {
  // Labels
  const officerLabel = useMemo(() => getOfficerLabel(), []);
  const officerPluralLabel = useMemo(() => getOfficerPluralLabel(), []);

  // ── Navigation state ─────────────────────────────────────────────
  const initialConfig = initialSubTab ? subTabConfig[initialSubTab] : null;
  const [pageShowString, setPageShowString] = useState(initialConfig?.page || 'showManagement');
  const [activeTabString, setActiveTabString] = useState(initialConfig?.activeTab || 'showToursTab');
  const [tourSubTab, setTourSubTab] = useState('tour');
  const [tourStopSubTab, setTourStopSubTab] = useState('details');

  // ── Grid data ────────────────────────────────────────────────────
  const [rows, setRows] = useState<KstDataGridRows>({ entries: null });
  const [tourStopTaskRows, setTourStopTaskRows] = useState<KstDataGridRows>({ entries: [] });

  // ── Pagers ───────────────────────────────────────────────────────
  const [tourListPager, setTourListPager] = useState<KstDataGridPager>({ curPage: null, numPages: null, records: null, isVisible: true });
  const [tourStopsForTourPager, setTourStopsForTourPager] = useState<KstDataGridPager>({ curPage: null, numPages: null, records: null, isVisible: true });
  const [tourStopsPager, setTourStopsPager] = useState<KstDataGridPager>({ curPage: null, numPages: null, records: null, isVisible: true });
  const [tasksPager, setTasksPager] = useState<KstDataGridPager>({ curPage: null, numPages: null, records: null, isVisible: true });
  const [tourStopTasksPager, setTourStopTasksPager] = useState<KstDataGridPager>({ curPage: null, numPages: null, records: null, isVisible: false });
  const [tourExemptionsPager, setTourExemptionsPager] = useState<KstDataGridPager>({ curPage: null, numPages: null, records: null, isVisible: true });
  const [tourLogsPager, setTourLogsPager] = useState<KstDataGridPager>({ curPage: null, numPages: null, records: null, isVisible: true });
  const [guardTourHistoryPager, setGuardTourHistoryPager] = useState<KstDataGridPager>({ curPage: null, numPages: null, records: null, isVisible: true });
  const [guardTourStopsForGuardTourPager, setGuardTourStopsForGuardTourPager] = useState<KstDataGridPager>({ curPage: null, numPages: null, records: null, isVisible: false });

  // ── Entity models ────────────────────────────────────────────────
  const [currentTour, setCurrentTour] = useState<any>(() => new Tour(null));
  const [currentTask, setCurrentTask] = useState<any>(() => new Task(null));
  const [currentTourStop, setCurrentTourStop] = useState<any>(() => new TourStop(null));
  const [currentTourExemption, setCurrentTourExemption] = useState<any>(() => new TourExemption(null));
  const [currentTourStopTask, setCurrentTourStopTask] = useState<any>(() => new TourStopTask(null));
  const [currentGuardTourStop, setCurrentGuardTourStop] = useState<any>(() => new GuardTourStop(null));
  const [currentUpdateOfficerComments, setCurrentUpdateOfficerComments] = useState<any>(() => new UpdateOfficerComments(null));

  // ── UI state ─────────────────────────────────────────────────────
  const [showAllTourStopsForTour, setShowAllTourStopsForTour] = useState(true);
  const [showGlobalTasksForTourStop, setShowGlobalTasksForTourStop] = useState(true);
  const [npsdClientList, setNpsdClientList] = useState<any[]>([]);
  const [selectableClientSites, setSelectableClientSites] = useState<any[]>([]);
  // isShowingClientAddressButton is computed (not state) — matches Vue computed property
  // Location-validation de-duplication is handled entirely by validatedLocationRef inside
  // validateTourStopLocation — when the cached value matches the current location string AND
  // coords are present, the function short-circuits with no API call.
  const validatedLocationRef = useRef<string | null>(null);

  // ── Task exemptions (for task edit form) ────────────────────────
  const [taskFormExemptions, setTaskFormExemptions] = useState<any[]>([]);
  const [taskFormExemptionsLoading, setTaskFormExemptionsLoading] = useState(false);

  // ── Loading states ───────────────────────────────────────────────
  const [toursLoading, setToursLoading] = useState(false);
  const [tourStopsLoading, setTourStopsLoading] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [exemptionsLoading, setExemptionsLoading] = useState(false);
  const [tourLogsLoading, setTourLogsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [guardTourStopsLoading, setGuardTourStopsLoading] = useState(false);

  // ── Company-wide tour stop reminder email ────────────────────────
  // Single recipient list for ALL tour stops in this company. Editable on the Reminders tab;
  // read-only display on the per-stop edit form. Backend storage: Company.tourStopReminderEmail.
  const [companyReminderEmail, setCompanyReminderEmail] = useState<string>('');
  const [companyReminderEmailLoading, setCompanyReminderEmailLoading] = useState(false);

  // ── Delete confirmation ──────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>({
    open: false, title: 'Delete', message: '', items: [], deleteType: '',
  });
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // ── Copy tour modal ───────────────────────────────────────────────
  const [copyTourModal, setCopyTourModal] = useState<CopyTourModalState>({
    open: false, withStops: true,
  });

  // ── Computed: show client address button (matches Vue computed) ──
  const isShowingClientAddressButton = !!(currentTourStop.client && currentTourStop.client.id);

  // ── Refs for geofence map ────────────────────────────────────────
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const geofencePolygonRef = useRef<any>(null);

  // ── Helper: update rows from API result (entries array) ──────────
  const updateRowsFromEntries = useCallback((result: any) => {
    const entries = (result.entries || []).map((entry: any) => ({ ...entry, isChecked: false }));
    setRows({ entries });
  }, []);

  // ── Helper: update rows from API result (rows array) ─────────────
  const updateRowsFromRows = useCallback((result: any) => {
    const entries = (result.rows || []).map((entry: any) => ({ ...entry, isChecked: false }));
    setRows({ entries });
  }, []);

  // ── Helper: update task list rows ────────────────────────────────
  const updateTaskListRows = useCallback((taskList: any[]) => {
    const sorted = [...taskList].sort((a, b) => (a.listIdx > b.listIdx) ? 1 : -1);
    const entries = sorted.map((entry, i) => ({
      ...entry,
      isChecked: false,
      listIdx: i + 1,
      isListEnabled: true,
    }));
    setTourStopTaskRows({ entries });
    setTourStopTasksPager(prev => ({ ...prev, isVisible: false }));
  }, []);

  // ── Helper: convert ms to HH:mm display ─────────────────────────
  const msToTimeDisplay = useCallback((ms: number | null): string => {
    if (ms == null || ms < 0) return '';
    const hours = Math.floor(ms / (60 * 60 * 1000));
    const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
    return (hours < 10 ? '0' : '') + hours + ':' + (minutes < 10 ? '0' : '') + minutes;
  }, []);

  // ── Helper: geofence defaults ────────────────────────────────────
  const getDefaultGeofenceCoordinates = useCallback((tourStop: any) => {
    const offset = 0.01;
    return [
      { lat: tourStop.locationLatitude - offset, lng: tourStop.locationLongitude + offset },
      { lat: tourStop.locationLatitude + offset, lng: tourStop.locationLongitude + offset },
      { lat: tourStop.locationLatitude + offset, lng: tourStop.locationLongitude - offset },
      { lat: tourStop.locationLatitude - offset, lng: tourStop.locationLongitude - offset },
    ];
  }, []);

  const createGeofencePolygon = useCallback((tourStop: any, generateDefault = true): any => {
    if (!hasValue(tourStop.geofence) && !generateDefault) return null;

    let coords;
    if (hasValue(tourStop.geofence)) {
      coords = tourStop.geofence.slice(0, tourStop.geofence.length - 1);
    } else {
      coords = getDefaultGeofenceCoordinates(tourStop);
    }
    return new google.maps.Polygon({
      paths: coords,
      strokeColor: '#FF0000',
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: '#FF0000',
      fillOpacity: 0.35,
      editable: true,
    });
  }, [getDefaultGeofenceCoordinates]);

  // ══════════════════════════════════════════════════════════════════
  // DATA LOADING
  // ══════════════════════════════════════════════════════════════════

  const loadTours = useCallback((page: number) => {
    setRows({ entries: [] });
    setToursLoading(true);
    api.getTours(1, '10000', `{"data":{"take":10000,"skip":0,"page":${page},"pageSize":10000}}`, (result: any) => {
      updateRowsFromEntries(result);
      setTourListPager({ curPage: result.curPage, numPages: result.numPages, records: result.records, isVisible: true });
      setToursLoading(false);
    });
  }, [updateRowsFromEntries]);

  const loadTourStopsForTour = useCallback((tourId: number) => {
    setRows({ entries: [] });
    setTourStopsLoading(true);
    api.getTourStopsForTour(tourId, (result: any) => {
      result.entries.sort((a: any, b: any) => (a.listIdx > b.listIdx) ? 1 : -1);
      for (let i = 0; i < result.entries.length; i++) {
        result.entries[i].listIdx = i + 1;
        result.entries[i].isListEnabled = true;
      }
      updateRowsFromEntries(result);
      setTourStopsForTourPager({ curPage: result.curPage, numPages: result.numPages, records: result.records, isVisible: true });
      setTourStopsLoading(false);
    });
  }, [updateRowsFromEntries]);

  const loadTourStops = useCallback((page: number) => {
    setRows({ entries: [] });
    setTourStopsLoading(true);
    api.getTourStops(page, '10000', `{"data":{"take":10000,"skip":0,"page":${page},"pageSize":10000}}`, (result: any) => {
      result.entries.sort((a: any, b: any) => (a.title > b.title) ? 1 : -1);
      updateRowsFromEntries(result);
      setTourStopsPager({ curPage: result.curPage, numPages: result.numPages, records: result.records, isVisible: true });
      setTourStopsLoading(false);
    });
  }, [updateRowsFromEntries]);

  const loadTourTasks = useCallback((page: number) => {
    setTasksLoading(true);
    api.getTourTasks(page, '10000', `{"data":{"take":10000,"skip":0,"page":${page},"pageSize":10000}}`, (result: any) => {
      result.rows.sort((a: any, b: any) => (a.name > b.name) ? 1 : -1);
      updateRowsFromRows(result);
      setTasksPager({ curPage: result.curPage, numPages: result.numPages, records: result.records, isVisible: true });
      setTasksLoading(false);
    });
  }, [updateRowsFromRows]);

  const loadTourExemptions = useCallback((page: number) => {
    setExemptionsLoading(true);
    api.getTourExemptions(`{"data":{"take":10000,"skip":0,"page":${page},"pageSize":10000}}`, (result: any) => {
      updateRowsFromRows(result);
      setTourExemptionsPager({ curPage: result.curPage, numPages: result.numPages, records: result.records, isVisible: true });
      setExemptionsLoading(false);
    });
  }, [updateRowsFromRows]);

  const loadTaskFormExemptions = useCallback(() => {
    setTaskFormExemptionsLoading(true);
    api.getTourExemptions(`{"data":{"take":10000,"skip":0,"page":1,"pageSize":10000}}`, (result: any) => {
      const enabled = (result.rows || []).filter((r: any) => r.isEnabled);
      setTaskFormExemptions(enabled);
      setTaskFormExemptionsLoading(false);
    });
  }, []);

  const loadAllTourLogs = useCallback((page: number) => {
    setTourLogsLoading(true);
    api.getTourLogEntries(`{"data":{"take":30,"skip":0,"page":${page},"pageSize":30}}`, false, (result: any) => {
      const entries = (result.logs || []).map((entry: any) => ({ ...entry, isChecked: false }));
      setRows({ entries });
      setTourLogsPager({ curPage: result.curPage, numPages: result.numPages, records: result.records, isVisible: true });
      setTourLogsLoading(false);
    });
  }, []);

  const loadGuardTourLogs = useCallback((page: number) => {
    setTourLogsLoading(true);
    api.getGuardTourLogEntries(`{"data":{"take":30,"skip":0,"page":${page},"pageSize":30}}`, false, (result: any) => {
      const entries = (result.logs || []).map((entry: any) => ({ ...entry, isChecked: false }));
      setRows({ entries });
      setTourLogsPager({ curPage: result.curPage, numPages: result.numPages, records: result.records, isVisible: true });
      setTourLogsLoading(false);
    });
  }, []);

  const loadGuardTourHistory = useCallback((page: number) => {
    setRows({ entries: [] });
    setHistoryLoading(true);
    api.getClosedGuardTours(page, 30, (result: any) => {
      updateRowsFromRows(result);
      setGuardTourHistoryPager({ curPage: result.curPage, numPages: result.numPages, records: result.records, isVisible: true });
      setHistoryLoading(false);
    });
  }, [updateRowsFromRows]);

  const loadGuardTourStopsForGuardTour = useCallback((guardTourId: number) => {
    setRows({ entries: [] });
    setGuardTourStopsLoading(true);
    api.getGuardTourStopsForGuardTour(guardTourId, (result: any) => {
      updateRowsFromEntries(result);
      setGuardTourStopsForGuardTourPager({ curPage: result.curPage, numPages: result.numPages, records: result.records, isVisible: false });
      setGuardTourStopsLoading(false);
    });
  }, [updateRowsFromEntries]);

  const loadGuardTourStop = useCallback((guardTourStopId: number) => {
    api.getGuardTourStop(guardTourStopId, (result: any) => {
      const stop = new GuardTourStop(result);
      stop.tourStopTaskList.sort((a: any, b: any) => (a.listIdx > b.listIdx) ? 1 : -1);
      setCurrentGuardTourStop(stop);
    });
  }, []);

  // ── Initial load ─────────────────────────────────────────────────
  useEffect(() => {
    api.listClientGroup((result: any) => {
      setNpsdClientList(result.npsdClientList || []);
      // Load data for the initial sub-tab (defaults to tours)
      const page = initialConfig?.page;
      if (page === 'showTourStopList') loadTourStops(1);
      else if (page === 'showTaskList') loadTourTasks(1);
      else if (page === 'showExemptionList') loadTourExemptions(1);
      else if (page === 'showAllTourLogs') loadAllTourLogs(1);
      else if (page === 'showGuardTourHistory') loadGuardTourHistory(1);
      else loadTours(1);
    });
  }, []);

  // ══════════════════════════════════════════════════════════════════
  // NAVIGATION HANDLERS
  // ══════════════════════════════════════════════════════════════════

  const showTourManagement = useCallback(() => {
    loadTours(1);
    setPageShowString('showManagement');
    setActiveTabString('showToursTab');
  }, [loadTours]);

  const showTourStopList = useCallback(() => {
    setPageShowString('showTourStopList');
    setActiveTabString('showTourStopsTab');
    loadTourStops(1);
  }, [loadTourStops]);

  const showTaskList = useCallback(() => {
    setPageShowString('showTaskList');
    setActiveTabString('showTourStopTasksTab');
    loadTourTasks(1);
  }, [loadTourTasks]);

  const showExemptionList = useCallback(() => {
    setPageShowString('showExemptionList');
    setActiveTabString('showExemptionsTab');
    loadTourExemptions(1);
  }, [loadTourExemptions]);

  const showAllTourLogs = useCallback(() => {
    setPageShowString('showAllTourLogs');
    setActiveTabString('showLogsTab');
    loadAllTourLogs(1);
  }, [loadAllTourLogs]);

  const showGuardTourLogs = useCallback(() => {
    setPageShowString('showGuardTourLogs');
    loadGuardTourLogs(1);
  }, [loadGuardTourLogs]);

  const showGuardTourHistory = useCallback(() => {
    setPageShowString('showGuardTourHistory');
    setActiveTabString('showGuardTourHistoryTab');
    loadGuardTourHistory(1);
  }, [loadGuardTourHistory]);

  const loadCompanyReminderEmail = useCallback(() => {
    setCompanyReminderEmailLoading(true);
    api.getCompanyTourStopReminderEmail((result: any) => {
      setCompanyReminderEmail((result && result.tourStopReminderEmail) || '');
      setCompanyReminderEmailLoading(false);
    });
  }, []);

  const showReminders = useCallback(() => {
    setPageShowString('showReminders');
    setActiveTabString('showRemindersTab');
    loadCompanyReminderEmail();
  }, [loadCompanyReminderEmail]);

  const handleSaveCompanyReminderEmail = useCallback(() => {
    const raw = companyReminderEmail || '';
    // Mirror backend validation rules: max 1000 chars, max 10 addresses,
    // per-address email format, reject CR/LF. Empty/whitespace is allowed (clears the field).
    if (raw.length > 1000) {
      App.showPopupNotification('Reminder email field cannot exceed 1000 characters.', 'warning');
      return;
    }
    const trimmed = raw.trim();
    if (trimmed.length > 0) {
      const entries = raw.split(',').map(s => s.trim()).filter(s => s.length > 0);
      if (entries.length > 10) {
        App.showPopupNotification('Reminder email field cannot have more than 10 addresses.', 'warning');
        return;
      }
      const emailRegex = /^.+@.+\..+$/;
      for (const entry of entries) {
        if (entry.indexOf('\r') >= 0 || entry.indexOf('\n') >= 0) {
          App.showPopupNotification('Reminder email addresses cannot contain newline characters.', 'warning');
          return;
        }
        if (!emailRegex.test(entry)) {
          App.showPopupNotification(`Reminder email field contains an invalid email address: ${entry}`, 'warning');
          return;
        }
      }
    }
    api.updateCompanyTourStopReminderEmail(raw, () => {
      App.showPopupNotification('Reminder settings saved.', 'success');
      // Refresh from server so the field reflects what was actually persisted (e.g., null after blank).
      loadCompanyReminderEmail();
    });
  }, [companyReminderEmail, loadCompanyReminderEmail]);

  const showGuardTourStopsForGuardTour = useCallback((guardTourId: number) => {
    setPageShowString('showGuardTourStopsForGuardTour');
    loadGuardTourStopsForGuardTour(guardTourId);
  }, [loadGuardTourStopsForGuardTour]);

  const showGuardTourStop = useCallback((guardTourStopId: number) => {
    setPageShowString('showGuardTourStop');
    loadGuardTourStop(guardTourStopId);
  }, [loadGuardTourStop]);

  // Register click handlers on window for grid template links
  useEffect(() => {
    (window as any)._tourMgmtShowGuardTourStops = (id: number) => showGuardTourStopsForGuardTour(id);
    (window as any)._tourMgmtShowGuardTourStop = (id: number) => showGuardTourStop(id);
    return () => {
      delete (window as any)._tourMgmtShowGuardTourStops;
      delete (window as any)._tourMgmtShowGuardTourStop;
    };
  }, [showGuardTourStopsForGuardTour, showGuardTourStop]);

  const onMobileTabChange = useCallback((tabId: string) => {
    if (tabId === 'showToursTab') showTourManagement();
    else if (tabId === 'showTourStopsTab') showTourStopList();
    else if (tabId === 'showTourStopTasksTab') showTaskList();
    else if (tabId === 'showExemptionsTab') showExemptionList();
    else if (tabId === 'showLogsTab') showAllTourLogs();
    else if (tabId === 'showGuardTourHistoryTab') showGuardTourHistory();
    else if (tabId === 'showRemindersTab') showReminders();
  }, [showTourManagement, showTourStopList, showTaskList, showExemptionList, showAllTourLogs, showGuardTourHistory, showReminders]);

  // ── Sync URL with active tab ────────────────────────────────────
  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    const slug = activeTabToSlug[activeTabString];
    if (slug) {
      window.history.replaceState(null, '', `/admin/tour-management/${slug}`);
    }
  }, [activeTabString]);

  // ══════════════════════════════════════════════════════════════════
  // TOUR CRUD
  // ══════════════════════════════════════════════════════════════════

  const handleAddTour = useCallback(() => {
    setCurrentTour(new Tour(null));
    setRows({ entries: [] });
    setShowAllTourStopsForTour(true);
    setPageShowString('showTour');
    setTourSubTab('tour');
  }, []);

  const handleEditTour = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select a tour to edit by clicking the checkbox.', 'warning');
      return;
    }
    if (checkedItems.length > 1) {
      App.showPopupNotification('Only one tour may be edited at a time.', 'warning');
      return;
    }
    const tourId = checkedItems[0].id;
    api.getTour(tourId, (tour: any) => {
      const t = new Tour(tour);
      // Convert ms to display strings for time pickers
      for (const item of t.tourAvailabilityList) {
        (item as any).startTimeDisplay = msToTimeDisplay(item.startTime);
        (item as any).endTimeDisplay = msToTimeDisplay(item.endTime);
      }
      setCurrentTour(t);
      loadTourStopsForTour(tourId);
      setPageShowString('showTour');
      setTourSubTab('tour');
    });
  }, [msToTimeDisplay, loadTourStopsForTour]);

  const handleSaveTour = useCallback(() => {
    if (!hasStringValue(currentTour.title)) {
      App.showPopupNotification('Tour title is required.', 'warning');
      return;
    }
    if (!hasStringValue(currentTour.type)) {
      App.showPopupNotification('Tour type is required.', 'warning');
      return;
    }
    if (!hasStringValue(currentTour.timeZoneId)) {
      App.showPopupNotification('Time zone is required.', 'warning');
      return;
    }

    // Convert time display strings back to ms
    const mmtMidnight = moment().clone().startOf('day');
    for (const item of currentTour.tourAvailabilityList) {
      const startDisplay = (item as any).startTimeDisplay || '';
      const endDisplay = (item as any).endTimeDisplay || '';
      if (startDisplay) {
        const startMoment = moment(moment().format('MM-DD-YYYY') + ' ' + startDisplay, 'MM-DD-YYYY HH:mm');
        item.startTime = startMoment.diff(mmtMidnight, 'milliseconds');
      }
      if (endDisplay) {
        const endMoment = moment(moment().format('MM-DD-YYYY') + ' ' + endDisplay, 'MM-DD-YYYY HH:mm');
        item.endTime = endMoment.diff(mmtMidnight, 'milliseconds');
      }
    }

    if (currentTour.id != null) {
      api.updateTour(currentTour, currentTour.id, () => {
        setCurrentTour(new Tour(null));
        showTourManagement();
      });
    } else {
      api.addTour(currentTour, () => {
        setCurrentTour(new Tour(null));
        showTourManagement();
      });
    }
  }, [currentTour, showTourManagement]);

  const handleDeleteTour = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select a tour to delete by clicking the checkbox.', 'warning');
      return;
    }
    setDeleteConfirm({
      open: true,
      title: 'Delete Tour',
      message: `Are you sure you want to delete ${checkedItems.length > 1 ? 'these tours' : 'this tour'}?`,
      items: checkedItems,
      deleteType: 'tour',
    });
  }, []);

  const handleCopyTour = useCallback(() => {
    if (currentTour.id == null) {
      App.showPopupNotification('Please save this as a new tour first.', 'warning');
      return;
    }
    setCopyTourModal({ open: true, withStops: true });
  }, [currentTour]);

  const handleCopyTourWithStopsChange = useCallback((withStops: boolean) => {
    setCopyTourModal(prev => ({ ...prev, withStops }));
  }, []);

  const handleConfirmCopyTour = useCallback(() => {
    const copiedTour = { ...currentTour };
    if (copyTourModal.withStops) {
      copiedTour.tourIdToCopyTourStops = copiedTour.id;
    } else {
      copiedTour.tourIdToCopyTourStops = null;
    }
    const suffix = ' Copy';
    const base = (copiedTour.title || '').substring(0, 255 - suffix.length);
    copiedTour.title = base + suffix;
    copiedTour.id = null;
    copiedTour.tourAvailabilityList = deepCopyProperties(currentTour.tourAvailabilityList);
    for (const item of copiedTour.tourAvailabilityList) {
      item.id = null;
    }

    // Convert time display strings back to ms before saving
    const mmtMidnight = moment().clone().startOf('day');
    for (const item of copiedTour.tourAvailabilityList) {
      const startDisplay = (item as any).startTimeDisplay || '';
      const endDisplay = (item as any).endTimeDisplay || '';
      if (startDisplay) {
        const startMoment = moment(moment().format('MM-DD-YYYY') + ' ' + startDisplay, 'MM-DD-YYYY HH:mm');
        item.startTime = startMoment.diff(mmtMidnight, 'milliseconds');
      }
      if (endDisplay) {
        const endMoment = moment(moment().format('MM-DD-YYYY') + ' ' + endDisplay, 'MM-DD-YYYY HH:mm');
        item.endTime = endMoment.diff(mmtMidnight, 'milliseconds');
      }
    }

    setCopyTourModal({ open: false, withStops: true });
    api.addTour(copiedTour, () => {
      setCurrentTour(new Tour(null));
      showTourManagement();
    });
  }, [currentTour, copyTourModal.withStops, showTourManagement]);

  const handleCancelCopyTour = useCallback(() => {
    setCopyTourModal({ open: false, withStops: true });
  }, []);

  const handleSwitchTourStatus = useCallback((id: any, newStatus: boolean) => {
    api.enableTour(id, newStatus, () => {
      setRows(prev => ({
        entries: (prev.entries || []).map((e: any) =>
          e.id === id ? { ...e, isEnabled: newStatus } : e),
      }));
    });
  }, []);

  const handleCloseTour = useCallback(() => {
    showTourManagement();
  }, [showTourManagement]);

  // ── Tour Availability ────────────────────────────────────────────

  const handleAddTourDay = useCallback(() => {
    const sundayValue = WeekDays.find((n: any) => n.value === 'Sunday')?.dayOfWeek;
    setCurrentTour((prev: any) => {
      const updated = { ...prev };
      const list = [...(updated.tourAvailabilityList || [])];
      list.push({
        startTime: null,
        endTime: null,
        startDayOfWeek: sundayValue,
        endDayOfWeek: sundayValue,
        id: null,
        guid: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
        startTimeDisplay: '',
        endTimeDisplay: '',
      });
      updated.tourAvailabilityList = list;
      return updated;
    });
  }, []);

  const handleRemoveTourDay = useCallback((guid: string) => {
    setCurrentTour((prev: any) => {
      const updated = { ...prev };
      updated.tourAvailabilityList = (updated.tourAvailabilityList || []).filter((item: any) => item.guid !== guid);
      return updated;
    });
  }, []);

  // ══════════════════════════════════════════════════════════════════
  // TOUR STOP CRUD
  // ══════════════════════════════════════════════════════════════════

  const currentTourStopRef = useRef(currentTourStop);
  useEffect(() => { currentTourStopRef.current = currentTourStop; }, [currentTourStop]);

  const editTourStopInternal = useCallback((tourStopId: number, isTourStopForTour: boolean, useCurrentStop: boolean) => {
    setShowGlobalTasksForTourStop(true);
    setTourStopSubTab('details');

    const finishEdit = (tourStop: any) => {
      setCurrentTourStop(tourStop);

      // Seed validation cache so an unchanged location on save skips geocoding
      if (hasStringValue(tourStop.location) &&
          hasValue(tourStop.locationLatitude) && hasValue(tourStop.locationLongitude)) {
        validatedLocationRef.current = tourStop.location;
      } else {
        validatedLocationRef.current = null;
      }

      // Load client sites
      if (tourStop.client && tourStop.client.id) {
        api.listClientGroupSite(tourStop.client.id, (siteResult: any) => {
          setSelectableClientSites(siteResult || []);
        }, () => {});
      }

      // Load task list
      updateTaskListRows(tourStop.tourStopTaskList || []);

      if (isTourStopForTour) {
        setPageShowString('showTourStopForTour');
      } else {
        setPageShowString('showTourStop');
      }
    };

    if (useCurrentStop) {
      // Use the current tour stop already in state via ref (avoids setState callback abuse)
      finishEdit(currentTourStopRef.current);
    } else {
      api.getTourStop(tourStopId, (result: any) => {
        const tourStop = new TourStop(result);
        // If endDateTime is Dec 31, 9999, display as empty (no end date)
        if (tourStop.endDateTime) {
          const endDate = new Date(tourStop.endDateTime);
          if (endDate.getFullYear() === 9999) {
            tourStop.endDateTime = null;
          }
        }
        finishEdit(tourStop);
      });
    }
  }, [updateTaskListRows]);

  const handleAddTourStop = useCallback(() => {
    const tourStop = new TourStop(null);
    setCurrentTourStop(tourStop);
    setSelectableClientSites([]);
    setShowGlobalTasksForTourStop(true);
    setTourStopSubTab('details');
    setTourStopTaskRows({ entries: [] });
    setPageShowString('showTourStop');
  }, []);

  const handleEditTourStop = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select a tour stop to edit by clicking the checkbox.', 'warning');
      return;
    }
    if (checkedItems.length > 1) {
      App.showPopupNotification('Only one tour stop may be edited at a time.', 'warning');
      return;
    }
    editTourStopInternal(checkedItems[0].id, false, false);
  }, [editTourStopInternal]);

  const handleEditTourStopForTour = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select a tour stop to edit by clicking the checkbox.', 'warning');
      return;
    }
    if (checkedItems.length > 1) {
      App.showPopupNotification('Only one tour stop may be edited at a time.', 'warning');
      return;
    }
    editTourStopInternal(checkedItems[0].id, true, false);
  }, [editTourStopInternal]);

  // Shared location validation — caches by location string so we don't re-validate unnecessarily
  const validateTourStopLocation = useCallback((opts?: { focusOnField?: boolean, showLoadingScreen?: boolean }): Promise<boolean> => {
    const { focusOnField = true, showLoadingScreen = false } = opts || {};
    if (!hasStringValue(currentTourStop.location)) return Promise.resolve(false);

    // Already validated this exact location — skip
    if (validatedLocationRef.current === currentTourStop.location &&
        hasValue(currentTourStop.locationLatitude) && hasValue(currentTourStop.locationLongitude)) {
      return Promise.resolve(true);
    }

    return validateAddressWithSuggestions({
      address: currentTourStop.location,
      isLocationRequired: true,
      errorMessage: addressValidationErrorMsg,
      showLoadingScreen,
      companyId: getCurrentCompanyId(),
      // Previously-saved coords for discrepancy detection on edits.
      previousLat: currentTourStop?.locationLatitude ?? null,
      previousLng: currentTourStop?.locationLongitude ?? null,
    }).then((results: any) => {
      if (results) {
        const locationField = document.getElementById('txtValidateableLocation') as HTMLInputElement | null;
        fillInAddressFromSuggestions(results, currentTourStop, locationField, DataObjectLocationFields.tourStop, focusOnField);
        validatedLocationRef.current = currentTourStop.location;
        // Use functional update to only merge location fields, preserving other pending state changes
        setCurrentTourStop((prev: any) => ({
          ...prev,
          location: currentTourStop.location,
          locationLatitude: currentTourStop.locationLatitude,
          locationLongitude: currentTourStop.locationLongitude,
          customLocationId: currentTourStop.customLocationId,
        }));
      }
      return true;
    }).catch(() => false);
  }, [currentTourStop]);

  const handleTourStopLocationBlurred = useCallback(() => {
    if (!hasStringValue(currentTourStop.location)) return;
    // De-duplication is handled inside validateTourStopLocation via validatedLocationRef:
    // an unchanged location string with coords already on the entity short-circuits with
    // no API call, so we don't need a separate "skip blur" gate here.
    validateTourStopLocation({ focusOnField: true });
  }, [currentTourStop, validateTourStopLocation]);

  const handleSaveTourStop = useCallback(() => {
    if (!hasStringValue(currentTourStop.title)) {
      App.showPopupNotification('Tour stop title is required.', 'warning');
      return;
    }
    if (!currentTourStop.client || !currentTourStop.client.id) {
      App.showPopupNotification('A client must be selected.', 'warning');
      return;
    }
    if (!hasStringValue(currentTourStop.location)) {
      App.showPopupNotification('Location is required.', 'warning');
      return;
    }
    if (!hasStringValue(currentTourStop.instructions)) {
      App.showPopupNotification('Instructions are required.', 'warning');
      return;
    }
    if (!currentTourStop.startDateTime) {
      App.showPopupNotification('Start date is required.', 'warning');
      return;
    }
    if (!currentTourStop.numTimesToComplete || currentTourStop.numTimesToComplete < 1) {
      App.showPopupNotification('Number of times to complete must be at least 1.', 'warning');
      return;
    }
    if (!currentTourStop.enabledDaysOfWeek) {
      App.showPopupNotification('Select at least one day of the week for this tour stop.', 'warning');
      return;
    }

    const doSave = () => {
      // Save geofence from polygon if enabled
      if (currentTourStop.isGeofenceEnabled && geofencePolygonRef.current) {
        const path = geofencePolygonRef.current.getPath();
        const coords: any[] = [];
        for (let i = 0; i < path.getLength(); i++) {
          const point = path.getAt(i);
          coords.push({ lat: point.lat(), lng: point.lng() });
        }
        // Close the polygon by duplicating first coordinate
        if (coords.length > 0) {
          coords.push({ ...coords[0] });
        }
        currentTourStop.geofence = coords;
      } else if (!currentTourStop.isGeofenceEnabled) {
        currentTourStop.geofence = null;
      }

      // Also save sensitive times back
      addSensitiveTimesToTask();

      // Prepare tour stop for save — re-hydrate as TourStop so SerializeToJson is available
      const tourStopToSave = new TourStop(currentTourStop);
      if (!tourStopToSave.endDateTime) {
        tourStopToSave.endDateTime = new Date(Date.UTC(9999, 11, 31, 0, 0, 0, 0));
      }

      // Clean client object for API
      delete tourStopToSave.client.allSites;
      delete tourStopToSave.client.name;

      const closeTourStopAfterSave = () => {
        if (pageShowString === 'showTourStopForTour') {
          setPageShowString('showTour');
          setTourSubTab('tourStopList');
          api.getTour(currentTour.id, (tour: any) => {
            const t = new Tour(tour);
            for (const item of t.tourAvailabilityList) {
              (item as any).startTimeDisplay = msToTimeDisplay(item.startTime);
              (item as any).endTimeDisplay = msToTimeDisplay(item.endTime);
            }
            setCurrentTour(t);
          });
          loadTourStopsForTour(currentTour.id);
        } else {
          setPageShowString('showTourStopList');
          loadTourStops(1);
        }
      };

      if (currentTourStop.id != null) {
        api.updateTourStop(tourStopToSave, () => {
          App.showPopupNotification('Tour Stop Updated', 'success');
          closeTourStopAfterSave();
        });
      } else {
        api.addTourStop(tourStopToSave, () => {
          closeTourStopAfterSave();
        });
      }
    };

    // Validate address to resolve coordinates before saving (uses cache if already validated)
    validateTourStopLocation({ focusOnField: false, showLoadingScreen: true }).then((success) => {
      if (success) doSave();
    });
  }, [currentTourStop, pageShowString, currentTour, msToTimeDisplay, loadTourStopsForTour, loadTourStops, validateTourStopLocation]);

  const handleDeleteTourStop = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select a tour stop to delete by clicking the checkbox.', 'warning');
      return;
    }
    setDeleteConfirm({
      open: true,
      title: 'Delete Tour Stop',
      message: `Are you sure you want to delete ${checkedItems.length > 1 ? 'these tour stops' : 'this tour stop'}?`,
      items: checkedItems,
      deleteType: 'tourStop',
    });
  }, []);

  const handleDeleteTourStopFromTour = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select a tour stop to remove by clicking the checkbox.', 'warning');
      return;
    }
    const removeFn = async (tourStopId: number) => {
      return new Promise((resolve, reject) => {
        api.removeTourStop(currentTour.id, tourStopId, resolve, reject);
      });
    };
    const params = checkedItems.map((item: any) => [item.id]);
    runPromiseFactoriesSequentially(removeFn, params).then(() => {
      loadTourStopsForTour(currentTour.id);
    });
  }, [currentTour, loadTourStopsForTour]);

  const handleSwitchTourStopStatus = useCallback((id: any, newStatus: boolean) => {
    api.enableTourStop(id, newStatus, () => {
      setRows(prev => ({
        entries: (prev.entries || []).map((e: any) =>
          e.id === id ? { ...e, isEnabled: newStatus } : e),
      }));
    });
  }, []);

  const handleCloseTourStop = useCallback(() => {
    if (pageShowString === 'showTourStopForTour') {
      setPageShowString('showTour');
      setTourSubTab('tourStopList');
      api.getTour(currentTour.id, (tour: any) => {
        const t = new Tour(tour);
        for (const item of t.tourAvailabilityList) {
          (item as any).startTimeDisplay = msToTimeDisplay(item.startTime);
          (item as any).endTimeDisplay = msToTimeDisplay(item.endTime);
        }
        setCurrentTour(t);
      });
      loadTourStopsForTour(currentTour.id);
    } else {
      setPageShowString('showTourStopList');
      loadTourStops(1);
    }
  }, [pageShowString, currentTour, msToTimeDisplay, loadTourStopsForTour, loadTourStops]);

  // ── Tour Stops for Tour ──────────────────────────────────────────

  const handleShowAllPotentialTourStops = useCallback(() => {
    setShowAllTourStopsForTour(false);
    loadTourStops(1);
  }, [loadTourStops]);

  const handleAddTourStopToTour = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select tour stops to add by clicking the checkbox.', 'warning');
      return;
    }
    const addFn = async (tourStopId: number) => {
      return new Promise((resolve, reject) => {
        api.addTourStopToTour(currentTour.id, tourStopId, resolve, reject);
      });
    };
    const params = checkedItems.map((item: any) => [item.id]);
    runPromiseFactoriesSequentially(addFn, params).then(() => {
      setShowAllTourStopsForTour(true);
      loadTourStopsForTour(currentTour.id);
    });
  }, [currentTour, loadTourStopsForTour]);

  const handleBackToStops = useCallback(() => {
    setShowAllTourStopsForTour(true);
    loadTourStopsForTour(currentTour.id);
  }, [currentTour, loadTourStopsForTour]);

  const handleReorderStopMoved = useCallback((row: any) => {
    // Get current rows and reorder
    setRows(prev => {
      const entries = [...prev.entries];
      const oldIdx = entries.findIndex((e: any) => e.id === row.id);
      if (oldIdx === -1) return prev;
      const newIdx = row.listIdx - 1;
      const [moved] = entries.splice(oldIdx, 1);
      entries.splice(newIdx, 0, moved);
      // Re-index
      for (let i = 0; i < entries.length; i++) {
        entries[i].listIdx = i + 1;
      }
      // Send reorder to server
      const tourStopIds = entries.map((e: any) => e.id);
      api.reorderTourStopsForTour(currentTour.id, tourStopIds, () => {});
      return { entries };
    });
  }, [currentTour]);

  // ══════════════════════════════════════════════════════════════════
  // TASK CRUD
  // ══════════════════════════════════════════════════════════════════

  const handleAddTask = useCallback(() => {
    setCurrentTask(new Task(null));
    loadTaskFormExemptions();
    setPageShowString('showTask');
  }, [loadTaskFormExemptions]);

  const handleEditTask = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select a task to edit by clicking the checkbox.', 'warning');
      return;
    }
    if (checkedItems.length > 1) {
      App.showPopupNotification('Only one task may be edited at a time.', 'warning');
      return;
    }
    loadTaskFormExemptions();
    api.getTask(checkedItems[0].id, (result: any) => {
      const tourExemptionIdList = (result.tourExemptionList || []).map((e: any) => e.id);
      setCurrentTask({ ...checkedItems[0], tourExemptionIdList, tourExemptionList: result.tourExemptionList || [] });
      setPageShowString('showTask');
    });
  }, [loadTaskFormExemptions]);

  const handleSaveTask = useCallback(() => {
    if (!hasStringValue(currentTask.name)) {
      App.showPopupNotification('Task name is required.', 'warning');
      return;
    }
    if (currentTask.id != null) {
      api.updateTask(currentTask, () => {
        App.showPopupNotification('Task saved successfully.', 'success');
        showTaskList();
      });
    } else {
      api.addTask(currentTask, () => {
        App.showPopupNotification('Task created successfully.', 'success');
        showTaskList();
      });
    }
  }, [currentTask, showTaskList]);

  const handleDeleteTask = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select a task to delete by clicking the checkbox.', 'warning');
      return;
    }
    const deleteFn = async (taskId: number) => {
      return new Promise((resolve) => {
        api.deleteTask(taskId, resolve);
      });
    };
    const params = checkedItems.map((item: any) => [item.id]);
    runPromiseFactoriesSequentially(deleteFn, params).then(() => {
      showTaskList();
    });
  }, [showTaskList]);

  // ── Tour Stop Tasks ──────────────────────────────────────────────

  const handleShowGlobalTasks = useCallback(() => {
    setShowGlobalTasksForTourStop(false);
    loadTourTasks(1);
  }, [loadTourTasks]);

  const handleAddTaskToTourStop = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select tasks to add by clicking the checkbox.', 'warning');
      return;
    }
    setCurrentTourStop((prev: any) => {
      const updated = { ...prev };
      const taskList = [...(updated.tourStopTaskList || [])];
      for (const item of checkedItems) {
        // Avoid duplicates
        if (taskList.some((t: any) => t.taskId === item.id)) continue;
        const newTask = new TourStopTask(null);
        newTask.taskId = item.id;
        newTask.name = item.name;
        newTask.description = item.defaultDescription;
        newTask.listIdx = taskList.length + 1;
        taskList.push(newTask);
      }
      updated.tourStopTaskList = taskList;
      updateTaskListRows(taskList);
      return updated;
    });
    setShowGlobalTasksForTourStop(true);
  }, [updateTaskListRows]);

  const handleDeleteTaskFromTourStop = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select tasks to remove by clicking the checkbox.', 'warning');
      return;
    }
    setCurrentTourStop((prev: any) => {
      const updated = { ...prev };
      const taskList = (updated.tourStopTaskList || []).filter(
        (t: any) => !checkedItems.some((ci: any) => ci.uniqueId === t.uniqueId)
      );
      updated.tourStopTaskList = taskList;
      updateTaskListRows(taskList);
      return updated;
    });
  }, [updateTaskListRows]);

  const handleEditTourStopTask = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select a task to edit by clicking the checkbox.', 'warning');
      return;
    }
    if (checkedItems.length > 1) {
      App.showPopupNotification('Only one task may be edited at a time.', 'warning');
      return;
    }
    const task = { ...checkedItems[0] };
    // Build DaysOfWeek structure for time sensitive editing
    const daysOfWeek = WeekDays.map((day: any) => ({
      ...day,
      TAGroups: [] as any[],
    }));
    if (hasValue(task.timeSensitiveList)) {
      for (const sensitive of task.timeSensitiveList) {
        const dayIdx = daysOfWeek.findIndex((d: any) => d.dayOfWeek === sensitive.startDayOfWeek);
        if (dayIdx >= 0) {
          daysOfWeek[dayIdx].TAGroups.push({
            ...sensitive,
            startTimeDisplay: msToTimeDisplay(sensitive.startTime),
            endTimeDisplay: msToTimeDisplay(sensitive.endTime),
          });
        }
      }
    }
    task.DaysOfWeek = daysOfWeek;
    setCurrentTourStopTask(task);
    setPageShowString('showTourStopTask');
  }, [msToTimeDisplay]);

  const handleBackToTourStops = useCallback(() => {
    setShowGlobalTasksForTourStop(true);
    updateTaskListRows(currentTourStop.tourStopTaskList || []);
  }, [currentTourStop, updateTaskListRows]);

  const handleReorderTaskMoved = useCallback((row: any) => {
    setTourStopTaskRows(prev => {
      const entries = [...prev.entries];
      const oldIdx = entries.findIndex((e: any) => e.uniqueId === row.uniqueId);
      if (oldIdx === -1) return prev;
      const newIdx = row.listIdx - 1;
      const [moved] = entries.splice(oldIdx, 1);
      entries.splice(newIdx, 0, moved);
      for (let i = 0; i < entries.length; i++) {
        entries[i].listIdx = i + 1;
      }
      // Also update currentTourStop's task list
      setCurrentTourStop((prevStop: any) => ({
        ...prevStop,
        tourStopTaskList: entries,
      }));
      return { entries };
    });
  }, []);

  const handleUseTourStopTask = useCallback(() => {
    // Save the time sensitive data back to the current tour stop's task list
    addSensitiveTimesToTask();

    // Navigate back to the tour stop edit
    if (currentTour.id != null) {
      editTourStopInternal(currentTourStop.id, true, true);
    } else {
      editTourStopInternal(currentTourStop.id, false, true);
    }
  }, [currentTour, currentTourStop, editTourStopInternal]);

  const handleCloseTourStopTask = useCallback(() => {
    if (currentTour.id != null) {
      editTourStopInternal(currentTourStop.id, true, true);
    } else {
      editTourStopInternal(currentTourStop.id, false, true);
    }
  }, [currentTour, currentTourStop, editTourStopInternal]);

  // ══════════════════════════════════════════════════════════════════
  // TOUR EXEMPTION CRUD
  // ══════════════════════════════════════════════════════════════════

  const handleAddTourExemption = useCallback(() => {
    setCurrentTourExemption(new TourExemption(null));
    setPageShowString('showExemption');
  }, []);

  const handleEditTourExemption = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select an exception to edit by clicking the checkbox.', 'warning');
      return;
    }
    if (checkedItems.length > 1) {
      App.showPopupNotification('Only one exception may be edited at a time.', 'warning');
      return;
    }
    setCurrentTourExemption({ ...checkedItems[0] });
    setPageShowString('showExemption');
  }, []);

  const handleSaveTourExemption = useCallback(() => {
    if (!hasStringValue(currentTourExemption.name)) {
      App.showPopupNotification('Exception name is required.', 'warning');
      return;
    }
    if (currentTourExemption.revisionGroup) {
      api.updateTourExemption(currentTourExemption, currentTourExemption.revisionGroup, () => {
        App.showPopupNotification('Exception saved successfully.', 'success');
        showExemptionList();
      });
    } else {
      api.saveTourExemption(currentTourExemption, () => {
        App.showPopupNotification('Exception created successfully.', 'success');
        showExemptionList();
      });
    }
  }, [currentTourExemption, showExemptionList]);

  const handleDeleteTourExemption = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select an exception to delete by clicking the checkbox.', 'warning');
      return;
    }
    setDeleteConfirm({
      open: true,
      title: 'Delete Exception',
      message: `Are you sure you want to delete ${checkedItems.length > 1 ? 'these exceptions' : 'this exception'}?`,
      items: checkedItems,
      deleteType: 'tourExemption',
    });
  }, []);

  const handleSwitchTourExemptionStatus = useCallback((revisionGroup: any, newStatus: boolean) => {
    api.EnableTourExemption(revisionGroup, newStatus, () => {
      // The grid keys the toggle on row.id when present, else row.revisionGroup —
      // match rows the same way so the update lands regardless of row shape.
      setRows(prev => ({
        entries: (prev.entries || []).map((e: any) =>
          (e.id != null ? e.id : e.revisionGroup) === revisionGroup ? { ...e, isEnabled: newStatus } : e),
      }));
    });
  }, []);

  // ══════════════════════════════════════════════════════════════════
  // TIME SENSITIVE
  // ══════════════════════════════════════════════════════════════════

  const handleAddTaskTimeSensitivePeriod = useCallback((day: any) => {
    setCurrentTourStopTask((prev: any) => {
      const updated = { ...prev };
      const daysOfWeek = [...updated.DaysOfWeek];
      const dayIdx = daysOfWeek.findIndex((d: any) => d.dayOfWeek === day.dayOfWeek);
      if (dayIdx >= 0) {
        daysOfWeek[dayIdx] = { ...daysOfWeek[dayIdx], TAGroups: [{
          startTime: null, endTime: null,
          startDayOfWeek: day.dayOfWeek, endDayOfWeek: day.dayOfWeek,
          guid: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substr(2, 9),
          startTimeDisplay: '', endTimeDisplay: '',
        }]};
      }
      updated.DaysOfWeek = daysOfWeek;
      return updated;
    });
  }, []);

  const handleRemoveTaskTimeSensitivePeriod = useCallback((guid: string, dayOfWeek: number) => {
    setCurrentTourStopTask((prev: any) => {
      const updated = { ...prev };
      const newTimeSensitiveList = (updated.timeSensitiveList || []).filter((ts: any) => ts.guid !== guid);
      updated.timeSensitiveList = newTimeSensitiveList;
      // The TAGroups are also cleared in the JSX via day.TAGroups = []
      return updated;
    });
  }, []);

  const addSensitiveTimesToTask = useCallback(() => {
    if (!currentTourStopTask || !currentTourStopTask.DaysOfWeek) return;

    const mmtMidnight = moment().clone().startOf('day');
    const newTimeSensitiveList: any[] = [];

    for (const day of currentTourStopTask.DaysOfWeek) {
      for (const sensitiveTask of day.TAGroups) {
        const startDisplay = sensitiveTask.startTimeDisplay || '';
        const endDisplay = sensitiveTask.endTimeDisplay || '';
        if (!startDisplay || !endDisplay) continue;

        const startTime = moment(moment().format('MM-DD-YYYY') + ' ' + startDisplay, 'MM-DD-YYYY HH:mm');
        const endTime = moment(moment().format('MM-DD-YYYY') + ' ' + endDisplay, 'MM-DD-YYYY HH:mm');

        if (startTime >= endTime) {
          App.showPopupNotification('All start times must be before their corresponding end times.', 'warning');
          return;
        }

        newTimeSensitiveList.push({
          ...sensitiveTask,
          startTime: startTime.diff(mmtMidnight, 'milliseconds'),
          endTime: endTime.diff(mmtMidnight, 'milliseconds'),
        });
      }
    }

    // Update the task in the currentTourStop's task list
    setCurrentTourStop((prev: any) => {
      const updated = { ...prev };
      const taskList = [...(updated.tourStopTaskList || [])];
      const idx = taskList.findIndex((t: any) => t.uniqueId === currentTourStopTask.uniqueId);
      if (idx >= 0) {
        taskList[idx] = { ...taskList[idx], timeSensitiveList: newTimeSensitiveList };
      }
      updated.tourStopTaskList = taskList;
      return updated;
    });
  }, [currentTourStopTask]);

  // ══════════════════════════════════════════════════════════════════
  // GEOFENCE
  // ══════════════════════════════════════════════════════════════════

  const pendingMapInitRef = useRef(false);

  const initMapFromDom = useCallback(() => {
    const mapElt = document.getElementById('map');
    if (!mapElt) {
      // DOM not ready yet — retry
      setTimeout(initMapFromDom, 100);
      return;
    }
    mapInstanceRef.current = new google.maps.Map(mapElt, {
      zoom: 13,
      center: { lat: currentTourStopRef.current.locationLatitude, lng: currentTourStopRef.current.locationLongitude },
      // Match the app theme at mount; the geofence map is recreated each time it opens.
      styles: getMapStyleForCurrentTheme(),
    });
    geofencePolygonRef.current.setMap(mapInstanceRef.current);
    mapInstanceRef.current.fitBounds(getGoogleMapsPolygonBounds(geofencePolygonRef.current));
  }, []);

  const initGeoFenceMap = useCallback(() => {
    // Create polygon if needed
    if (!geofencePolygonRef.current) {
      geofencePolygonRef.current = createGeofencePolygon(currentTourStop);
    }

    // Request map initialization after next render
    pendingMapInitRef.current = true;
    // Force a state update so the useEffect fires
    setTourStopSubTab((prev) => prev);
  }, [currentTourStop, createGeofencePolygon]);

  // Initialize map after DOM renders
  useEffect(() => {
    if (pendingMapInitRef.current) {
      pendingMapInitRef.current = false;
      initMapFromDom();
    }
  });

  const handleGeoFenceTabClicked = useCallback(() => {
    if (!currentTourStop.isGeofenceEnabled) return;

    // Validate location first (uses cache if already validated)
    if (!hasValue(currentTourStop.locationLatitude) || !hasValue(currentTourStop.locationLongitude)) {
      if (!hasStringValue(currentTourStop.location)) {
        App.showPopupNotification('Please set a valid location before configuring the geo-fence.', 'warning');
        return;
      }
      validateTourStopLocation({ focusOnField: false, showLoadingScreen: true }).then((success) => {
        if (success) initGeoFenceMap();
      });
      return;
    }

    initGeoFenceMap();
  }, [currentTourStop, validateTourStopLocation, initGeoFenceMap]);

  const handleSwitchGeoFence = useCallback((isEnabled: boolean) => {
    if (isEnabled) {
      const doInit = () => {
        geofencePolygonRef.current = createGeofencePolygon(currentTourStop);
        // Request map initialization after next render
        pendingMapInitRef.current = true;
        setTourStopSubTab((prev) => prev);
      };

      if (!hasValue(currentTourStop.locationLatitude) || !hasValue(currentTourStop.locationLongitude)) {
        if (!hasStringValue(currentTourStop.location)) {
          App.showPopupNotification('Please set a valid location before enabling the geo-fence.', 'warning');
          setCurrentTourStop((prev: any) => ({ ...prev, isGeofenceEnabled: false }));
          return;
        }
        validateTourStopLocation({ focusOnField: false, showLoadingScreen: true }).then((success) => {
          if (success) {
            doInit();
          } else {
            setCurrentTourStop((prev: any) => ({ ...prev, isGeofenceEnabled: false }));
          }
        });
        return;
      }
      doInit();
    } else {
      geofencePolygonRef.current = null;
      mapInstanceRef.current = null;
    }
  }, [currentTourStop, createGeofencePolygon, validateTourStopLocation]);

  const handleResetGeoFence = useCallback(() => {
    geofencePolygonRef.current = createGeofencePolygon(currentTourStop, false);
    if (!geofencePolygonRef.current) {
      // No existing geofence, create default
      geofencePolygonRef.current = createGeofencePolygon(currentTourStop, true);
    }
    handleGeoFenceTabClicked();
  }, [currentTourStop, createGeofencePolygon, handleGeoFenceTabClicked]);

  const handleShowSitesForClient = useCallback((clientId: number) => {
    if (!hasValue(clientId)) {
      setSelectableClientSites([]);
      return;
    }
    api.listClientGroupSite(clientId, (result: any) => {
      setSelectableClientSites(result || []);
    }, () => {});
  }, []);

  const handleOnSitesChanged = useCallback((sites: any[]) => {
    setCurrentTourStop((prev: any) => {
      const updated = { ...prev, client: { ...prev.client, sites } };
      // Auto-fill location from first selected site if location is empty
      if (sites && sites.length > 0 && !hasStringValue(prev.location)) {
        const firstSite = selectableClientSites.find((s: any) => s.id === sites[0]);
        if (firstSite && firstSite.location) {
          updated.location = firstSite.location;
        }
      }
      return updated;
    });
  }, [selectableClientSites]);

  const handleUpdateClientAddressFromServer = useCallback(() => {
    if (!currentTourStop.client || !currentTourStop.client.id) return;
    api.getNPSDClientAddress(currentTourStop.client.id, (result: any) => {
      if (result && result.locFormattedAddress) {
        validatedLocationRef.current = result.locFormattedAddress;
        setCurrentTourStop((prev: any) => ({
          ...prev,
          location: result.locFormattedAddress,
          locationLatitude: result.locationLatitude,
          locationLongitude: result.locationLongitude,
        }));
      }
    });
  }, [currentTourStop]);

  // ══════════════════════════════════════════════════════════════════
  // HISTORY
  // ══════════════════════════════════════════════════════════════════

  const handleShowUpdateOfficerComments = useCallback((completionId: number, officerComments: string, guardTourStopId: number) => {
    setCurrentUpdateOfficerComments({
      id: completionId,
      officerComments: officerComments || '',
      guardTourStopId,
    });
    setPageShowString('updateOfficerComments');
  }, []);

  const handleUpdateOfficerComment = useCallback(() => {
    api.updateCompleteGuardStop(currentUpdateOfficerComments.id, currentUpdateOfficerComments.officerComments, () => {
      App.showPopupNotification('Comments updated successfully.', 'success');
      showGuardTourStop(currentUpdateOfficerComments.guardTourStopId);
    });
  }, [currentUpdateOfficerComments, showGuardTourStop]);

  const getGoogleMapsImageSrcForCoordinates = useCallback((lat: number, lng: number) => {
    return `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=300x300&markers=color:red%7C${lat},${lng}&key=${GOOGLE_MAPS_BROWSER_API_KEY}`;
  }, []);

  // ══════════════════════════════════════════════════════════════════
  // DELETE CONFIRMATION
  // ══════════════════════════════════════════════════════════════════

  const handleConfirmDelete = useCallback(() => {
    const { deleteType, items } = deleteConfirm;

    setIsDeleting(true);
    if (deleteType === 'tour') {
      api.deleteTour(items[0].id, () => {
        setIsDeleting(false);
        showTourManagement();
      });
    } else if (deleteType === 'tourStop') {
      const deleteFn = async (id: number) => {
        return new Promise((resolve, reject) => {
          api.deleteTourStop(id, resolve, (err: any) => {
            reject(err);
            App.showPopupNotification('Error deleting tour stop.', 'error');
          });
        });
      };
      const params = items.map((item: any) => [item.id]);
      runPromiseFactoriesSequentially(deleteFn, params).then(() => {
        setIsDeleting(false);
        loadTourStops(1);
      });
    } else if (deleteType === 'tourExemption') {
      for (const item of items) {
        api.deleteTourExemption(item.revisionGroup, () => {});
      }
      setIsDeleting(false);
      setTimeout(() => showExemptionList(), 800);
    }

    setDeleteConfirm({ open: false, title: 'Delete', message: '', items: [], deleteType: '' });
  }, [deleteConfirm, showTourManagement, loadTourStops, showExemptionList]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, title: 'Delete', message: '', items: [], deleteType: '' });
  }, []);

  // ══════════════════════════════════════════════════════════════════
  // GRID BUTTON COMMAND DISPATCHERS
  // ══════════════════════════════════════════════════════════════════

  const handleTourListCommand = useCallback((fn: string, items: any[], page: number) => {
    if (fn === 'add') handleAddTour();
    else if (fn === 'edit') handleEditTour(items);
    else if (fn === 'delete') handleDeleteTour(items);
  }, [handleAddTour, handleEditTour, handleDeleteTour]);

  const handleTourStopsForTourCommand = useCallback((fn: string, items: any[]) => {
    if (fn === 'add' || fn === 'add-new') handleShowAllPotentialTourStops();
    else if (fn === 'edit') handleEditTourStopForTour(items);
    else if (fn === 'delete') handleDeleteTourStopFromTour(items);
  }, [handleShowAllPotentialTourStops, handleEditTourStopForTour, handleDeleteTourStopFromTour]);

  const handleGlobalTourStopsCommand = useCallback((fn: string, items: any[]) => {
    if (fn === 'add') handleAddTourStopToTour(items);
    else if (fn === 'edit') handleEditTourStopForTour(items);
    else if (fn === 'delete') handleDeleteTourStopFromTour(items);
    else if (fn === 'back') handleBackToStops();
  }, [handleAddTourStopToTour, handleEditTourStopForTour, handleDeleteTourStopFromTour, handleBackToStops]);

  const handleTourStopListCommand = useCallback((fn: string, items: any[]) => {
    if (fn === 'add') handleAddTourStop();
    else if (fn === 'edit') handleEditTourStop(items);
    else if (fn === 'delete') handleDeleteTourStop(items);
  }, [handleAddTourStop, handleEditTourStop, handleDeleteTourStop]);

  const handleTourStopTasksCommand = useCallback((fn: string, items: any[]) => {
    if (fn === 'add') handleShowGlobalTasks();
    else if (fn === 'edit') handleEditTourStopTask(items);
    else if (fn === 'delete') handleDeleteTaskFromTourStop(items);
  }, [handleShowGlobalTasks, handleEditTourStopTask, handleDeleteTaskFromTourStop]);

  const handleGlobalTasksCommand = useCallback((fn: string, items: any[]) => {
    if (fn === 'add') handleAddTaskToTourStop(items);
    else if (fn === 'back') handleBackToTourStops();
  }, [handleAddTaskToTourStop, handleBackToTourStops]);

  // ── Associated Stops & Assign to Stops Modals ──────────────────

  const [associatedStopsModal, setAssociatedStopsModal] = useState<{ open: boolean; task: any }>({ open: false, task: null });
  const [assignToStopsModal, setAssignToStopsModal] = useState<{ open: boolean; tasks: any[] }>({ open: false, tasks: [] });

  const handleShowAssociatedStops = useCallback((row: any) => {
    setAssociatedStopsModal({ open: true, task: row });
  }, []);

  const handleCloseAssociatedStops = useCallback(() => {
    setAssociatedStopsModal({ open: false, task: null });
  }, []);

  const handleAssignMoreFromAssociated = useCallback(() => {
    const task = associatedStopsModal.task;
    setAssociatedStopsModal({ open: false, task: null });
    setAssignToStopsModal({ open: true, tasks: [task] });
  }, [associatedStopsModal.task]);

  const handleOpenAssignToStops = useCallback((checkedItems: any[]) => {
    if (checkedItems.length === 0) {
      App.showPopupNotification('Please select at least one task by clicking the checkbox.', 'warning');
      return;
    }
    setAssignToStopsModal({ open: true, tasks: checkedItems });
  }, []);

  const handleCloseAssignToStops = useCallback(() => {
    setAssignToStopsModal({ open: false, tasks: [] });
  }, []);

  const handleAssignToStopsComplete = useCallback(() => {
    loadTourTasks(1);
  }, [loadTourTasks]);

  const handleAssociatedStopsChanged = useCallback(() => {
    loadTourTasks(1);
  }, [loadTourTasks]);

  const handleTaskCellClick = useCallback((payload: { row: any; column: any; field: string }) => {
    if (payload.field === 'numAssociatedTourStops') {
      handleShowAssociatedStops(payload.row);
    }
  }, [handleShowAssociatedStops]);

  const handleTaskListCommand = useCallback((fn: string, items: any[]) => {
    if (fn === 'add') handleAddTask();
    else if (fn === 'edit') handleEditTask(items);
    else if (fn === 'delete') handleDeleteTask(items);
    else if (fn === 'assignToStops') handleOpenAssignToStops(items);
  }, [handleAddTask, handleEditTask, handleDeleteTask, handleOpenAssignToStops]);

  const handleExemptionListCommand = useCallback((fn: string, items: any[]) => {
    if (fn === 'add') handleAddTourExemption();
    else if (fn === 'edit') handleEditTourExemption(items);
    else if (fn === 'delete') handleDeleteTourExemption(items);
  }, [handleAddTourExemption, handleEditTourExemption, handleDeleteTourExemption]);

  // ══════════════════════════════════════════════════════════════════
  // RETURN
  // ══════════════════════════════════════════════════════════════════

  return {
    // Labels
    officerLabel,
    officerPluralLabel,

    // Navigation
    pageShowString,
    activeTabString,
    tourSubTab, setTourSubTab,
    tourStopSubTab, setTourStopSubTab,

    // Data
    rows,
    tourStopTaskRows,

    // Pagers
    tourListPager,
    tourStopsForTourPager,
    tourStopsPager,
    tasksPager,
    tourStopTasksPager,
    tourExemptionsPager,
    tourLogsPager,
    guardTourHistoryPager,
    guardTourStopsForGuardTourPager,

    // Entities
    currentTour, setCurrentTour,
    currentTask, setCurrentTask,
    currentTourStop, setCurrentTourStop,
    currentTourExemption, setCurrentTourExemption,
    currentTourStopTask, setCurrentTourStopTask,
    currentGuardTourStop,
    currentUpdateOfficerComments, setCurrentUpdateOfficerComments,

    // UI state
    showAllTourStopsForTour,
    showGlobalTasksForTourStop,
    npsdClientList,
    selectableClientSites,
    isShowingClientAddressButton,
    taskFormExemptions,
    taskFormExemptionsLoading,

    // Loading
    toursLoading,
    tourStopsLoading,
    tasksLoading,
    exemptionsLoading,
    tourLogsLoading,
    historyLoading,
    guardTourStopsLoading,

    // Delete modal
    isDeleting,
    deleteConfirm,
    handleConfirmDelete,
    handleCancelDelete,

    // Refs
    mapContainerRef,

    // Navigation handlers
    showTourManagement,
    showTourStopList,
    showTaskList,
    showExemptionList,
    showAllTourLogs,
    showGuardTourLogs,
    showGuardTourHistory,
    showReminders,
    showGuardTourStopsForGuardTour,
    showGuardTourStop,
    onMobileTabChange,

    // Reminders tab (company-wide)
    companyReminderEmail, setCompanyReminderEmail,
    companyReminderEmailLoading,
    handleSaveCompanyReminderEmail,

    // Tour handlers
    handleSaveTour,
    handleCopyTour,
    copyTourModal,
    handleCopyTourWithStopsChange,
    handleConfirmCopyTour,
    handleCancelCopyTour,
    handleSwitchTourStatus,
    handleCloseTour,
    handleAddTourDay,
    handleRemoveTourDay,

    // Tour Stop handlers
    handleSaveTourStop,
    handleSwitchTourStopStatus,
    handleCloseTourStop,
    handleShowAllPotentialTourStops,
    handleReorderStopMoved,

    // Task handlers
    handleSaveTask,
    handleTaskCellClick,

    // Task-to-Stop modal state
    associatedStopsModal,
    handleCloseAssociatedStops,
    handleAssignMoreFromAssociated,
    handleAssociatedStopsChanged,
    assignToStopsModal,
    handleCloseAssignToStops,
    handleAssignToStopsComplete,

    // Tour Stop Task handlers
    handleUseTourStopTask,
    handleCloseTourStopTask,
    handleReorderTaskMoved,
    handleAddTaskTimeSensitivePeriod,
    handleRemoveTaskTimeSensitivePeriod,

    // Exemption handlers
    handleSaveTourExemption,
    handleSwitchTourExemptionStatus,

    // Geofence
    handleGeoFenceTabClicked,
    handleSwitchGeoFence,
    handleResetGeoFence,

    // Location
    handleTourStopLocationBlurred,
    handleShowSitesForClient,
    handleOnSitesChanged,
    handleUpdateClientAddressFromServer,

    // History
    handleShowUpdateOfficerComments,
    handleUpdateOfficerComment,
    getGoogleMapsImageSrcForCoordinates,

    // Grid command dispatchers
    handleTourListCommand,
    handleTourStopsForTourCommand,
    handleGlobalTourStopsCommand,
    handleTourStopListCommand,
    handleTourStopTasksCommand,
    handleGlobalTasksCommand,
    handleTaskListCommand,
    handleExemptionListCommand,

    // Page loaders (for grid pagination)
    loadTours,
    loadTourStopsForTour,
    loadTourStops,
    loadTourTasks,
    loadTourExemptions,
    loadAllTourLogs,
    loadGuardTourLogs,
    loadGuardTourHistory,
  };
}
